import { X, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useCart } from "@/lib/cartContext";
import { cn } from "@/lib/utils";

const colorHex: Record<string, string> = {
  nero: "#1c1c1c", bianco: "#f5f5f5", avorio: "#fffff0", grigio: "#9e9e9e",
  navy: "#001f5b", caramello: "#c68642", cipria: "#f2c8c4", cognac: "#8b4513",
  bordeaux: "#4a0018", beige: "#f5f0e8", verde: "#2d4a3e", oro: "#b8973a",
  nude: "#e8c9a0", blu: "#1e3a5f", azzurro: "#6fa8dc", cammello: "#c19a6b",
};

export function CartDrawer() {
  const { items, cartOpen, closeCart, removeItem, updateQuantity, itemCount, total } = useCart();

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm transition-opacity duration-300",
          cartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div
        className={cn(
          "fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-white flex flex-col shadow-2xl transition-transform duration-400 ease-out",
          cartOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <ShoppingBag className="size-4 text-stone-900" />
            <span className="font-medium text-base sm:text-lg md:font-semibold tracking-tight text-stone-900">
              Il tuo carrello
            </span>
            {itemCount > 0 && (
              <span className="bg-stone-900 text-white text-[10px] font-bold rounded-full size-5 flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="p-2 text-stone-400 hover:text-stone-900 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-5 text-center px-8">
              <ShoppingBag className="size-10 text-stone-200" />
              <p className="text-stone-400 text-sm">Il tuo carrello è vuoto</p>
              <button
                onClick={closeCart}
                className="text-[10px] tracking-widest uppercase border border-stone-300 px-8 py-3 hover:border-stone-900 transition-colors"
              >
                Continua lo shopping
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {items.map(({ key, product, quantity, selectedSize, selectedColor }) => (
                <li key={key} className="flex gap-4 px-6 py-5">
                  {/* Product image */}
                  <NavLink
                    to={`/product/${product.id}`}
                    onClick={closeCart}
                    className="flex-shrink-0 w-20 bg-stone-100 overflow-hidden block"
                    style={{ height: "6rem" }}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </NavLink>

                  {/* Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <p className="text-[10px] tracking-widest uppercase text-stone-400 mb-0.5">
                        {product.brand || product.category}
                      </p>
                      <NavLink
                        to={`/product/${product.id}`}
                        onClick={closeCart}
                        className="text-sm font-medium text-stone-900 leading-snug hover:text-stone-600 transition-colors line-clamp-2"
                      >
                        {product.name}
                      </NavLink>

                      {/* Taglia e colore */}
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {selectedSize && (
                          <span className="text-[10px] tracking-wide text-stone-500 border border-stone-200 px-2 py-0.5">
                            Taglia {selectedSize}
                          </span>
                        )}
                        {selectedColor && (
                          <span className="flex items-center gap-1.5 text-[10px] tracking-wide text-stone-500">
                            <span
                              className="size-3 rounded-full border border-stone-200 inline-block flex-shrink-0"
                              style={{ background: colorHex[selectedColor.toLowerCase()] ?? "#ccc" }}
                            />
                            {selectedColor.charAt(0).toUpperCase() + selectedColor.slice(1)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      {/* Qty controls */}
                      <div className="flex items-center border border-stone-200">
                        <button
                          onClick={() => updateQuantity(key, quantity - 1)}
                          className="px-2.5 py-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors"
                        >
                          <Minus className="size-3" />
                        </button>
                        <span className="px-3 py-1.5 text-xs font-medium text-stone-900 min-w-[2rem] text-center border-x border-stone-200">
                          {quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(key, quantity + 1)}
                          className="px-2.5 py-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors"
                        >
                          <Plus className="size-3" />
                        </button>
                      </div>

                      <div className="text-right">
                        <p className="text-sm font-semibold text-stone-900">
                          €{(product.price * quantity).toLocaleString("it-IT")}
                        </p>
                        {product.originalPrice && (
                          <p className="text-[10px] text-stone-400 line-through">
                            €{(product.originalPrice * quantity).toLocaleString("it-IT")}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeItem(key)}
                    className="self-start mt-0.5 text-stone-300 hover:text-stone-900 transition-colors p-1"
                    aria-label="Rimuovi"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-stone-100 px-6 py-5 space-y-4 bg-white">
            <div className="flex items-center justify-between">
              <span className="text-xs tracking-widest uppercase text-stone-500">Subtotale</span>
              <span className="text-base font-semibold text-stone-900">
                €{total.toLocaleString("it-IT")}
              </span>
            </div>
            <p className="text-[10px] text-stone-400 tracking-wide">
              Spedizione e imposte calcolate al checkout
            </p>

            <NavLink
              to="/checkout"
              onClick={closeCart}
              className="group flex items-center justify-center gap-2 w-full bg-stone-900 text-white text-xs tracking-widest uppercase py-4 hover:bg-stone-700 transition-colors font-medium"
            >
              Procedi al checkout
              <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
            </NavLink>

            <NavLink
              to="/cart"
              onClick={closeCart}
              className="block text-center text-[10px] tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors py-1"
            >
              Visualizza carrello completo
            </NavLink>
          </div>
        )}
      </div>
    </>
  );
}
